import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useColumnPreferences } from '../../context/ColumnPreferencesContext';
import SearchFilterPanel from '../../components/SearchFilterPanel';
const ViewCostPage = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showColumnConfig, setShowColumnConfig] = useState(false);
    const { preferences } = useColumnPreferences();
    // Search and filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilters, setActiveFilters] = useState({});
    // Reference data for filters
    const [classifications, setClassifications] = useState([]);
    const [materialTypes, setMaterialTypes] = useState([]);
    const [productionLines, setProductionLines] = useState([]);
    const [doorModelGroups, setDoorModelGroups] = useState([]);
    const [doorModels, setDoorModels] = useState([]);
    const [modelTypes, setModelTypes] = useState([]);
    const [sizeLengths, setSizeLengths] = useState([]);
    const [sizeWidths, setSizeWidths] = useState([]);
    // Drag state for column reordering
    const [draggedColumn, setDraggedColumn] = useState(null);
    const [dragOverColumn, setDragOverColumn] = useState(null);
    // Load saved column preferences from localStorage
    const loadColumnPreferences = () => {
        const defaultColumns = [
            { id: 'classification_name', label: 'Classification', visible: true, width: 'w-32' },
            { id: 'material_type_name', label: 'Construction', visible: true, width: 'w-40' },
            { id: 'door_model_group_name', label: 'Door Model', visible: true, width: 'w-48' },
            { id: 'door_model_name', label: 'Series', visible: true, width: 'w-32' },
            { id: 'model_type_name', label: 'Type', visible: true, width: 'w-44' },
            { id: 'production_line_name', label: 'Production Line', visible: true, width: 'w-36' },
            { id: 'size_width_name', label: 'Width', visible: true, width: 'w-24' },
            { id: 'size_length_name', label: 'Height', visible: true, width: 'w-24' },
            { id: 'cogs', label: 'Material Cost', visible: true, width: 'w-28' },
            { id: 'cushion_v1', label: preferences.cushion_v1, visible: true, width: 'w-28' },
            { id: 'cushion_v2', label: preferences.cushion_v2, visible: true, width: 'w-28' },
            { id: 'cushion_v3', label: preferences.cushion_v3, visible: true, width: 'w-28' },
            { id: 'cushion_v4', label: preferences.cushion_v4, visible: true, width: 'w-28' },
            { id: 'total_cogs', label: 'Total Material Cost', visible: true, width: 'w-32' },
            { id: 'divisor_d1', label: preferences.divisor_d1, visible: true, width: 'w-28' },
            { id: 'list_price_calculated', label: 'List Price', visible: true, width: 'w-32' },
        ];
        try {
            const saved = localStorage.getItem('viewCostColumnPreferences');
            if (saved) {
                const savedPreferences = JSON.parse(saved);
                // Rebuild columns in saved order, handling new columns
                const orderedColumns = [];
                const processedIds = new Set();
                // First, add columns in saved order
                savedPreferences.forEach((savedCol) => {
                    const defaultCol = defaultColumns.find(d => d.id === savedCol.id);
                    if (defaultCol) {
                        orderedColumns.push({ ...defaultCol, visible: savedCol.visible });
                        processedIds.add(savedCol.id);
                    }
                });
                // Then, add any new columns that weren't in saved preferences
                defaultColumns.forEach(col => {
                    if (!processedIds.has(col.id)) {
                        orderedColumns.push(col);
                    }
                });
                return orderedColumns;
            }
        }
        catch (error) {
            console.error('Error loading column preferences:', error);
        }
        return defaultColumns;
    };
    const [columns, setColumns] = useState(loadColumnPreferences());
    // Refresh columns when preferences change
    useEffect(() => {
        setColumns(prev => {
            // Update labels while preserving order and visibility
            return prev.map(col => {
                if (col.id === 'cushion_v1')
                    return { ...col, label: preferences.cushion_v1 };
                if (col.id === 'cushion_v2')
                    return { ...col, label: preferences.cushion_v2 };
                if (col.id === 'cushion_v3')
                    return { ...col, label: preferences.cushion_v3 };
                if (col.id === 'cushion_v4')
                    return { ...col, label: preferences.cushion_v4 };
                if (col.id === 'divisor_d1')
                    return { ...col, label: preferences.divisor_d1 };
                return col;
            });
        });
    }, [preferences]);
    // Fetch reference data for filters
    const fetchReferenceData = async () => {
        try {
            const [class_res, mat_res, prod_res, grp_res, mod_res, mtype_res, len_res, wid_res] = await Promise.all([
                fetch('/api/pricing/classifications', { credentials: 'include' }),
                fetch('/api/pricing/material_types', { credentials: 'include' }),
                fetch('/api/pricing/production_lines', { credentials: 'include' }),
                fetch('/api/pricing/door_model_groups', { credentials: 'include' }),
                fetch('/api/pricing/door_models', { credentials: 'include' }),
                fetch('/api/pricing/model_types', { credentials: 'include' }),
                fetch('/api/pricing/size_lengths', { credentials: 'include' }),
                fetch('/api/pricing/size_widths', { credentials: 'include' }),
            ]);
            const [class_data, mat_data, prod_data, grp_data, mod_data, mtype_data, len_data, wid_data] = await Promise.all([
                class_res.json(),
                mat_res.json(),
                prod_res.json(),
                grp_res.json(),
                mod_res.json(),
                mtype_res.json(),
                len_res.json(),
                wid_res.json(),
            ]);
            setClassifications(class_data.data || []);
            setMaterialTypes(mat_data.data || []);
            setProductionLines(prod_data.data || []);
            setDoorModelGroups(grp_data.data || []);
            setDoorModels(mod_data.data || []);
            setModelTypes(mtype_data.data || []);
            setSizeLengths(len_data.data || []);
            setSizeWidths(wid_data.data || []);
        }
        catch (err) {
            console.error('Failed to load reference data:', err);
        }
    };
    useEffect(() => {
        fetchReferenceData();
        fetchData();
    }, []);
    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch('/api/pricing-configs', {
                credentials: 'include',
            });
            if (!response.ok) {
                throw new Error('Failed to fetch cost configurations');
            }
            const result = await response.json();
            setData(result.data || []);
        }
        catch (err) {
            setError(err.message || 'Failed to load data');
            console.error('Fetch error:', err);
        }
        finally {
            setLoading(false);
        }
    };
    const toggleColumn = (columnId) => {
        setColumns(prev => {
            const updated = prev.map(col => col.id === columnId ? { ...col, visible: !col.visible } : col);
            // Save to localStorage
            try {
                localStorage.setItem('viewCostColumnPreferences', JSON.stringify(updated));
            }
            catch (error) {
                console.error('Error saving column preferences:', error);
            }
            return updated;
        });
    };
    const toggleAllColumns = (visible) => {
        setColumns(prev => {
            const updated = prev.map(col => ({ ...col, visible }));
            // Save to localStorage
            try {
                localStorage.setItem('viewCostColumnPreferences', JSON.stringify(updated));
            }
            catch (error) {
                console.error('Error saving column preferences:', error);
            }
            return updated;
        });
    };
    // Column reordering handlers
    const handleDragStart = (e, columnId) => {
        setDraggedColumn(columnId);
        e.dataTransfer.effectAllowed = 'move';
    };
    const handleDragOver = (e, columnId) => {
        e.preventDefault();
        if (draggedColumn && draggedColumn !== columnId) {
            setDragOverColumn(columnId);
        }
    };
    const handleDragLeave = () => {
        setDragOverColumn(null);
    };
    const handleDrop = (e, targetColumnId) => {
        e.preventDefault();
        if (!draggedColumn || draggedColumn === targetColumnId) {
            setDraggedColumn(null);
            setDragOverColumn(null);
            return;
        }
        setColumns(prev => {
            const newColumns = [...prev];
            const draggedIndex = newColumns.findIndex(col => col.id === draggedColumn);
            const targetIndex = newColumns.findIndex(col => col.id === targetColumnId);
            if (draggedIndex === -1 || targetIndex === -1)
                return prev;
            // Remove dragged column and insert at target position
            const [draggedCol] = newColumns.splice(draggedIndex, 1);
            newColumns.splice(targetIndex, 0, draggedCol);
            // Save to localStorage
            try {
                localStorage.setItem('viewCostColumnPreferences', JSON.stringify(newColumns));
            }
            catch (error) {
                console.error('Error saving column preferences:', error);
            }
            return newColumns;
        });
        setDraggedColumn(null);
        setDragOverColumn(null);
    };
    const handleDragEnd = () => {
        setDraggedColumn(null);
        setDragOverColumn(null);
    };
    const resetColumnOrder = () => {
        const defaultColumns = [
            { id: 'classification_name', label: 'Classification', visible: true, width: 'w-32' },
            { id: 'material_type_name', label: 'Construction', visible: true, width: 'w-40' },
            { id: 'door_model_group_name', label: 'Door Model', visible: true, width: 'w-48' },
            { id: 'door_model_name', label: 'Series', visible: true, width: 'w-32' },
            { id: 'model_type_name', label: 'Type', visible: true, width: 'w-44' },
            { id: 'production_line_name', label: 'Production Line', visible: true, width: 'w-36' },
            { id: 'size_width_name', label: 'Width', visible: true, width: 'w-24' },
            { id: 'size_length_name', label: 'Height', visible: true, width: 'w-24' },
            { id: 'cogs', label: 'Material Cost', visible: true, width: 'w-28' },
            { id: 'cushion_v1', label: preferences.cushion_v1, visible: true, width: 'w-28' },
            { id: 'cushion_v2', label: preferences.cushion_v2, visible: true, width: 'w-28' },
            { id: 'cushion_v3', label: preferences.cushion_v3, visible: true, width: 'w-28' },
            { id: 'cushion_v4', label: preferences.cushion_v4, visible: true, width: 'w-28' },
            { id: 'total_cogs', label: 'Total Material Cost', visible: true, width: 'w-32' },
            { id: 'divisor_d1', label: preferences.divisor_d1, visible: true, width: 'w-28' },
            { id: 'list_price_calculated', label: 'List Price', visible: true, width: 'w-32' },
        ];
        setColumns(defaultColumns);
        try {
            localStorage.setItem('viewCostColumnPreferences', JSON.stringify(defaultColumns));
        }
        catch (error) {
            console.error('Error saving column preferences:', error);
        }
    };
    const visibleColumns = useMemo(() => columns.filter(col => col.visible), [columns]);
    // Build filter configuration from reference data
    const filterConfigs = useMemo(() => [
        {
            key: 'classification_id',
            label: 'Classification',
            type: 'multi-select',
            options: classifications.map(c => ({ value: c.id, label: c.name })),
        },
        {
            key: 'material_type_id',
            label: 'Construction',
            type: 'multi-select',
            options: materialTypes.map(m => ({ value: m.id, label: m.name })),
        },
        {
            key: 'production_line_id',
            label: 'Production Line',
            type: 'multi-select',
            options: productionLines.map(p => ({ value: p.id, label: p.name })),
        },
        {
            key: 'door_model_group_id',
            label: 'Door Model',
            type: 'multi-select',
            options: doorModelGroups.map(g => ({ value: g.id, label: g.name })),
        },
        {
            key: 'door_model_id',
            label: 'Series',
            type: 'multi-select',
            options: doorModels.map(m => ({
                value: m.id,
                label: m.model_group_name ? `${m.model_group_name} - ${m.name}` : m.name
            })),
        },
        {
            key: 'model_type_id',
            label: 'Type',
            type: 'multi-select',
            options: modelTypes.map(t => ({ value: t.id, label: t.name })),
        },
        {
            key: 'size_width_id',
            label: 'Width',
            type: 'multi-select',
            options: sizeWidths.map(w => ({ value: w.id, label: w.display_name || w.name })),
        },
        {
            key: 'size_length_id',
            label: 'Height',
            type: 'multi-select',
            options: sizeLengths.map(l => ({ value: l.id, label: l.display_name || l.name })),
        },
        {
            key: 'cogs',
            label: 'Material Cost ($)',
            type: 'range',
            options: [],
        },
        {
            key: 'list_price_calculated',
            label: 'List Price ($)',
            type: 'range',
            options: [],
        },
    ], [classifications, materialTypes, productionLines, doorModelGroups, doorModels, modelTypes, sizeWidths, sizeLengths]);
    // Filter data based on search query and active filters
    const filteredData = useMemo(() => {
        let result = [...data];
        // Apply text search across multiple fields
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            result = result.filter(config => {
                const searchableFields = [
                    config.classification_name,
                    config.material_type_name,
                    config.production_line_name,
                    config.door_model_group_name,
                    config.door_model_name,
                    config.model_type_name,
                    config.size_width_name,
                    config.size_length_name,
                    config.cogs?.toString(),
                    config.total_cogs?.toString(),
                    config.list_price_calculated?.toString(),
                ];
                return searchableFields.some(field => field && field.toLowerCase().includes(query));
            });
        }
        // Apply active filters
        Object.entries(activeFilters).forEach(([key, value]) => {
            if (Array.isArray(value) && value.length > 0) {
                // Multi-select filter
                result = result.filter(config => value.includes(config[key]));
            }
            else if (typeof value === 'object' && value !== null) {
                // Range filter
                const range = value;
                result = result.filter(config => {
                    const configValue = config[key];
                    if (configValue === undefined || configValue === null)
                        return false;
                    if (range.min !== undefined && configValue < range.min)
                        return false;
                    if (range.max !== undefined && configValue > range.max)
                        return false;
                    return true;
                });
            }
        });
        return result;
    }, [data, searchQuery, activeFilters]);
    const handleFiltersChange = useCallback((newFilters) => {
        setActiveFilters(newFilters);
    }, []);
    const handleSearchChange = useCallback((query) => {
        setSearchQuery(query);
    }, []);
    const formatValue = (value, columnId) => {
        if (value === null || value === undefined)
            return '-';
        // Format currency fields (Material Cost, Standard Cost, and List Price)
        if (['cogs', 'total_cogs', 'list_price_calculated'].includes(columnId)) {
            return typeof value === 'number' ? `$${value.toFixed(2)}` : String(value);
        }
        // Format decimal fields (Cushions and Divisor)
        if (['cushion_v1', 'cushion_v2', 'cushion_v3', 'cushion_v4', 'divisor_d1'].includes(columnId)) {
            return typeof value === 'number' ? value.toFixed(2) : String(value);
        }
        return String(value);
    };
    return (_jsxs("div", { className: "min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50", children: [_jsx("div", { className: "bg-white border-b border-secondary-200 sticky top-0 z-30 shadow-sm", children: _jsx("div", { className: "px-6 py-4", children: _jsxs("div", { className: "flex items-center justify-between mb-4", children: [_jsxs("div", { children: [_jsxs(Link, { to: "/dashboard", className: "inline-flex items-center text-sm text-primary-600 hover:text-primary-700 mb-2 transition-colors", children: [_jsx("svg", { className: "w-4 h-4 mr-1", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M10 19l-7-7m0 0l7-7m-7 7h18" }) }), "Back to Dashboard"] }), _jsx("h1", { className: "text-3xl font-bold text-secondary-900", children: "View Cost" }), _jsx("p", { className: "text-secondary-600 mt-1", children: "Comprehensive view of all cost configurations with configurable columns" })] }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsxs("div", { className: "relative", children: [_jsxs("button", { onClick: () => setShowColumnConfig(!showColumnConfig), className: "btn-secondary flex items-center gap-2", children: [_jsx("svg", { className: "w-5 h-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" }) }), "Configure Columns", _jsxs("span", { className: "text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full", children: [visibleColumns.length, "/", columns.length] })] }), showColumnConfig && (_jsxs(_Fragment, { children: [_jsx("div", { className: "fixed inset-0 z-40", onClick: () => setShowColumnConfig(false) }), _jsxs("div", { className: "absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-secondary-200 z-50", children: [_jsxs("div", { className: "p-4 border-b border-secondary-200", children: [_jsx("h3", { className: "font-semibold text-secondary-900 mb-2", children: "Show/Hide & Reorder Columns" }), _jsx("p", { className: "text-xs text-secondary-500 mb-3", children: "Drag columns to reorder them" }), _jsxs("div", { className: "flex gap-2 flex-wrap", children: [_jsx("button", { onClick: () => toggleAllColumns(true), className: "text-xs px-3 py-1 bg-primary-50 text-primary-700 hover:bg-primary-100 rounded transition-colors", children: "Show All" }), _jsx("button", { onClick: () => toggleAllColumns(false), className: "text-xs px-3 py-1 bg-secondary-50 text-secondary-700 hover:bg-secondary-100 rounded transition-colors", children: "Hide All" }), _jsx("button", { onClick: resetColumnOrder, className: "text-xs px-3 py-1 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded transition-colors", children: "Reset Order" })] })] }), _jsx("div", { className: "max-h-96 overflow-y-auto p-2", children: columns.map(col => (_jsxs("div", { draggable: true, onDragStart: (e) => handleDragStart(e, col.id), onDragOver: (e) => handleDragOver(e, col.id), onDragLeave: handleDragLeave, onDrop: (e) => handleDrop(e, col.id), onDragEnd: handleDragEnd, className: `flex items-center gap-3 px-3 py-2 hover:bg-secondary-50 rounded cursor-move transition-colors ${dragOverColumn === col.id ? 'bg-primary-50 border-2 border-primary-300 border-dashed' : ''} ${draggedColumn === col.id ? 'opacity-50' : ''}`, children: [_jsx("svg", { className: "w-4 h-4 text-secondary-400 flex-shrink-0", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M4 8h16M4 16h16" }) }), _jsxs("label", { className: "flex items-center gap-3 flex-1 cursor-pointer", children: [_jsx("input", { type: "checkbox", checked: col.visible, onChange: () => toggleColumn(col.id), className: "w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500" }), _jsx("span", { className: "text-sm text-secondary-700", children: col.label })] })] }, col.id))) })] })] }))] }), _jsx("button", { onClick: fetchData, disabled: loading, className: "btn-secondary", title: "Refresh data", children: _jsx("svg", { className: `w-5 h-5 ${loading ? 'animate-spin' : ''}`, fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" }) }) }), _jsx(Link, { to: "/pricing/configurations", className: "btn-secondary", children: "Manage Cost \u2192" })] })] }) }) }), _jsxs("div", { className: "px-6 py-6", children: [_jsx(SearchFilterPanel, { filters: filterConfigs, activeFilters: activeFilters, onFiltersChange: handleFiltersChange, searchQuery: searchQuery, onSearchChange: handleSearchChange, totalCount: data.length, filteredCount: filteredData.length, searchPlaceholder: "Search by classification, construction, series, type, dimensions, cost..." }), _jsx("div", { className: "mb-4 flex items-center justify-between", children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "text-sm text-secondary-600", children: "Total Cost Configurations:" }), _jsx("span", { className: "px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold", children: data.length.toLocaleString() }), filteredData.length !== data.length && (_jsxs("span", { className: "text-sm text-secondary-500", children: ["(showing ", filteredData.length.toLocaleString(), " filtered)"] }))] }) }), error && (_jsx("div", { className: "bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4", children: error })), loading ? (_jsx("div", { className: "flex items-center justify-center py-20", children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" }), _jsx("p", { className: "text-secondary-600", children: "Loading cost configurations..." })] }) })) : filteredData.length === 0 ? (_jsxs("div", { className: "bg-white rounded-lg shadow-sm border border-secondary-200 p-12 text-center", children: [_jsx("svg", { className: "w-16 h-16 text-secondary-300 mx-auto mb-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" }) }), _jsx("h3", { className: "text-lg font-semibold text-secondary-900 mb-2", children: searchQuery || Object.keys(activeFilters).length > 0 ? 'No results found' : 'No cost configurations yet' }), _jsx("p", { className: "text-secondary-600 mb-4", children: searchQuery || Object.keys(activeFilters).length > 0 ? 'Try adjusting your search or filters' : 'Create your first cost configuration to get started' }), !searchQuery && Object.keys(activeFilters).length === 0 && (_jsx(Link, { to: "/pricing/configurations", className: "btn-primary", children: "Create Configuration" }))] })) : (_jsx("div", { className: "bg-white rounded-lg shadow-sm border border-secondary-200 overflow-hidden", children: _jsx("div", { className: "overflow-x-auto", children: _jsxs("table", { className: "min-w-full divide-y divide-secondary-200", children: [_jsx("thead", { className: "bg-secondary-50", children: _jsx("tr", { children: visibleColumns.map(col => (_jsx("th", { className: "px-4 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider whitespace-nowrap", children: col.label }, col.id))) }) }), _jsx("tbody", { className: "bg-white divide-y divide-secondary-200", children: filteredData.map((row) => (_jsx("tr", { className: "hover:bg-secondary-50 transition-colors", children: visibleColumns.map(col => (_jsx("td", { className: "px-4 py-3 text-sm text-secondary-900 whitespace-nowrap", children: formatValue(row[col.id], col.id) }, col.id))) }, row.id))) })] }) }) }))] })] }));
};
export default ViewCostPage;
